import COMMON from 'constants/common';
import React, { useEffect, useState } from 'react';
import Layout from 'components/system/layouts/Layout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import MachineConfContent from 'components/machineConf/organisms/contents/MachineConfContent';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ReturnButton } from 'components/system/atoms';
import NormalButton from 'components/system/atoms/buttons/NormalButton';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getClassificationName } from 'utils/common.helper';
import CLSFCN from 'constants/classification';
import redirectLinks from 'constants/redirectLinks';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleCallSystemError } from 'redux/slices/commonSlice';
import ERROR_CODE from 'constants/errorCode';
import { SaveDetailInfoType } from 'types/machineConf/stationSelectType';
import UrlHelper from 'utils/url.helper';

/* ************ Context ************ */
const ScreenContextValue = {
  title: 'サウンド/照明演出',
  screenId: COMMON.SCREEN_ID.LED_SOUND_CONF_DUO,
  currentMenu: COMMON.MENU.MACHINE_CONF,
  disableContents: true,
};

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    minWidth: '1080px',
    minHeight: '563px',
    margin: '18px 4px',
  },
  buttonArea: {
    display: 'grid',
    gap: '10px',
    width: '164px',
    height: '154px',
  },
  returnButton: {
    fontSize: '20px',
    lineHeight: '24px',
    width: '157px',
    height: '70px',
    zIndex: 200,
  },
  button: {
    width: '157px',
    height: '70px',
    background: '#EBEBEB',
    margin: '0 0 1px auto',
    fontSize: '20px',
    boxShadow: '5px 5px 3px rgba(0, 0, 0, 0.2)',
    textAlign: 'left',
    lineHeight: '24px',
    justifyContent: 'flex-start',
    padding: '6px 8px',
  },

  mainArea: {
    width: '1000px',
    gap: '10px',
  },
  description: {
    fontSize: '23px',
    padding: '34px 94px',
    height: '94px',
  },
  middleContent: {
    display: 'flex',
    margin: '0 10px 0 29px',
    '& .soundArea': {
      width: '445px',
      height: '430px',
    },
    '& .ledArea': {
      width: '450px',
      height: '230px',
      marginLeft: '18px',
    },
  },
  table: {
    width: '100%',
    '& th,td': {
      border: '1px solid #707070',
      fontSize: '23px',
      minHeight: '74px',
    },
    '& th': {
      textAlign: 'center',
      padding: 0,
    },
    '& tbody tr': {},
    '& td': {
      padding: '1px 2px',
      '& .cellStyle': {
        fontSize: '16px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        marginTop: 15,
      },
    },
    '& td.bgGray': {
      backgroundColor: '#EEEEEE',
    },
  },
  grayCover: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(128, 128, 128, 0.5)',
    zIndex: 100,
  },
  rgbDiv: {
    width: '46px',
  },
  grayCoverDark: {
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(128, 128, 128, 1)',
    zIndex: 100,
  },
}));

/* ******** Constants ******** */

/* ******** Main Component ******** */
/**
 *
 * @returns {React.FC} - 照明演出設定
 *
 */
const LedSoundConfDuo: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [savedConfProps] = useState(
    location.state !== undefined && location.state !== null ? (location.state as SaveDetailInfoType) : null,
  );
  const dispatch = useAppDispatch();

  /* ************ State ************ */
  // Global
  const machineDuoConf = useAppSelector((state) => state.machineConf.machineDuoConf);
  const ledSoundConfDuo = useAppSelector((state) => state.machineConf.machineDuoConf.ledSoundConfDuo);
  const { selectedStationList } = useAppSelector((state) => state.machineConf);
  const { confId: selfConfId } = selectedStationList[0];
  // 保存設定一覧画面から遷移の場合true
  const isSaveSetting = machineDuoConf?.conf?.confDiv === COMMON.CONF_DIV.SAVE_CONF;
  // 遷移元が保存設定(全設定送信確認)かをチェック
  const checkWay = useAppSelector((state) => state.machineConf.checkWay);
  const isAllCheckActive = machineDuoConf?.conf?.confDiv === COMMON.CONF_DIV.SAVE_CONF && checkWay === '1';

  /* ************ Event ************ */
  // 画面表示時
  useEffect(() => {
    // グローバルステートが見つからない場合、システムエラー画面に遷移
    if (!ledSoundConfDuo) {
      dispatch(handleCallSystemError({ errorNo: ERROR_CODE.NOT_FOUND }));
    }
  }, []);

  // 戻るボタン
  const handleClickReturn = () => {
    navigate(
      UrlHelper.convertQueryUrlFrontEnd(redirectLinks.MCONF_DUO_HOME_DETAIL, {
        selectConfId: savedConfProps ? 'none' : selfConfId.toString(),
      }),
    );
  };
  // 共通ボタン
  const handleClickCommon = () => {
    navigate(redirectLinks.MCONF_DUO_CONF_COMMON);
  };

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <MachineConfContent savedConfProps={savedConfProps}>
          <Box className={classes.root}>
            <Box className={classes.buttonArea}>
              <ReturnButton className={classes.returnButton} onClick={handleClickReturn}>
                機器設定
                <br /> 詳細
              </ReturnButton>
              {!isSaveSetting && (
                <NormalButton className={classes.button} onClick={handleClickCommon}>
                  共通
                </NormalButton>
              )}
            </Box>

            <Box className={classes.mainArea}>
              <Box className={classes.description}>「サウンド」と「照明」の設定内容です。</Box>
              <Box className={classes.middleContent}>
                <Box className="soundArea">
                  <Table className={classes.table} aria-label="table">
                    <TableHead
                      sx={{
                        backgroundColor: '#B3B3B3',
                        '& .MuiTableCell-head': {
                          fontSize: '19px',
                        },
                      }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            width: '100px',
                          }}>
                          設定項目
                        </TableCell>
                        <TableCell
                          colSpan={2}
                          sx={{
                            width: '159px',
                          }}>
                          かんたん
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{
                            width: '184px',
                          }}>
                          くわしい
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody
                      sx={{
                        '& .MuiTableCell-root:not(.bgGray)': {
                          backgroundColor: '#FFFFFF',
                        },
                        '& .MuiTableCell-body': {
                          fontSize: '15px',
                        },
                      }}>
                      <TableRow>
                        <TableCell
                          className="bgGray"
                          rowSpan={16}
                          sx={{
                            width: '100px',
                            textAlign: 'center',
                          }}>
                          サウンド
                        </TableCell>

                        <TableCell className="bgGray" sx={{ width: '73px', position: 'relative' }}>
                          全体音量
                          {!isSaveSetting && ledSoundConfDuo?.soundConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && (
                            <div className={classes.grayCover} style={{ width: '159px', height: '98px' }} />
                          )}
                          {isAllCheckActive && (
                            <div className={classes.grayCoverDark} style={{ width: '344px', height: '391px' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ width: '86px' }}>{ledSoundConfDuo?.simpleVolAll}</TableCell>
                        <TableCell className="bgGray" sx={{ width: '82px', position: 'relative' }}>
                          全体音量
                          {!isSaveSetting && ledSoundConfDuo?.soundConfigType === COMMON.DUO_CONFIG_TYPE.SIMPLE && (
                            <div className={classes.grayCover} style={{ width: '184px', height: '391px' }} />
                          )}
                        </TableCell>
                        <TableCell colSpan={2} sx={{ width: '102px' }}>
                          {ledSoundConfDuo?.detailVolAll}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          BGM選択
                        </TableCell>
                        <TableCell sx={{}}>
                          {ledSoundConfDuo?.simpleBgm &&
                            getClassificationName(CLSFCN.GROUP_BGM.CATEGORY_CD, ledSoundConfDuo.simpleBgm)}
                        </TableCell>
                        <TableCell className="bgGray" sx={{}}>
                          BGM選択
                        </TableCell>
                        <TableCell colSpan={2} sx={{}}>
                          {ledSoundConfDuo?.detailAttractBgm &&
                            getClassificationName(CLSFCN.GROUP_BGM.CATEGORY_CD, ledSoundConfDuo.detailAttractBgm)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          BGM
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.simpleVolBgm}</TableCell>
                        <TableCell colSpan={3} rowSpan={2} sx={{ position: 'relative', textAlign: 'center' }}>
                          ー
                          {ledSoundConfDuo?.soundConfigType === COMMON.DUO_CONFIG_TYPE.DETAIL && (
                            <div className={classes.grayCover} style={{ width: '184px', height: '49px' }} />
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          効果音
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.simpleVolEffect}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={2} rowSpan={12} sx={{ position: 'relative', textAlign: 'center' }}>
                          ー
                          <div className={classes.grayCover} style={{ width: '159px', height: '293px' }} />
                        </TableCell>
                        <TableCell className="bgGray" rowSpan={4} sx={{}}>
                          アトラクト
                        </TableCell>
                        <TableCell className="bgGray" sx={{ width: '60px' }}>
                          BGM
                        </TableCell>
                        <TableCell sx={{ width: '42px' }}>{ledSoundConfDuo?.detailAttractVolBgm}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          効果音
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailAttractVolEffect}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          ボイス
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailAttractVolVoice}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          エラー
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailAttractVolError}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" rowSpan={4} sx={{}}>
                          ゲーム
                        </TableCell>
                        <TableCell className="bgGray" sx={{}}>
                          BGM
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailGameVolBgm}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          効果音
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailGameVolEffect}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          ボイス
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailGameVolVoice}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          エラー
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailGameVolError}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" rowSpan={4} sx={{}}>
                          獲得演出
                        </TableCell>
                        <TableCell className="bgGray" sx={{}}>
                          BGM
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailEarnVolBgm}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          効果音
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailEarnVolEffect}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          ボイス
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailEarnVolVoice}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          エラー
                        </TableCell>
                        <TableCell sx={{}}>{ledSoundConfDuo?.detailEarnVolError}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
                <Box className="ledArea">
                  <Table className={classes.table} aria-label="table">
                    <TableHead
                      sx={{
                        backgroundColor: '#B3B3B3',
                        '& .MuiTableCell-head': {
                          fontSize: '19px',
                        },
                      }}>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            width: '202px',
                          }}>
                          設定項目
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '252px',
                          }}>
                          内容
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody
                      sx={{
                        '& .MuiTableCell-root:not(.bgGray)': {
                          backgroundColor: '#FFFFFF',
                        },
                        '& .MuiTableCell-root': {
                          fontSize: '15px',
                        },
                      }}>
                      <TableRow>
                        <TableCell
                          className="bgGray"
                          rowSpan={5}
                          sx={{
                            width: '40spx',
                          }}>
                          照明
                        </TableCell>
                        <TableCell className="bgGray" sx={{ width: '160px' }}>
                          照明カラー
                        </TableCell>
                        <TableCell sx={{ height: '68px' }}>
                          {ledSoundConfDuo?.colorClass !== CLSFCN.DUO_COLOR_CLASS.CUSTOM ? (
                            <div>
                              {ledSoundConfDuo?.colorClass &&
                                getClassificationName(CLSFCN.DUO_COLOR_CLASS.CATEGORY_CD, ledSoundConfDuo.colorClass)}
                              /
                              {ledSoundConfDuo?.ledDirecting &&
                                getClassificationName(
                                  CLSFCN.DUO_LED_DIRECTING.CATEGORY_CD,
                                  ledSoundConfDuo.ledDirecting,
                                )}
                            </div>
                          ) : (
                            <div>
                              <div>
                                {getClassificationName(CLSFCN.DUO_COLOR_CLASS.CATEGORY_CD, ledSoundConfDuo?.colorClass)}
                                /
                              </div>
                              <div style={{ display: 'flex' }}>
                                <div style={{ width: '100px' }}>メインカラー</div>
                                <div className={classes.rgbDiv}>R:{ledSoundConfDuo?.customMainR}</div>
                                <div className={classes.rgbDiv}>G:{ledSoundConfDuo?.customMainG}</div>
                                <div className={classes.rgbDiv}>B:{ledSoundConfDuo?.customMainB}</div>
                              </div>
                              <div style={{ display: 'flex' }}>
                                <div style={{ width: '100px' }}>サブカラー</div>
                                <div className={classes.rgbDiv}>R:{ledSoundConfDuo?.customSubR} </div>
                                <div className={classes.rgbDiv}>G:{ledSoundConfDuo?.customSubG} </div>
                                <div className={classes.rgbDiv}>B:{ledSoundConfDuo?.customSubB}</div>
                              </div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          フレームホワイト
                        </TableCell>
                        <TableCell sx={{}}>
                          {ledSoundConfDuo?.frameWhite &&
                            getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, ledSoundConfDuo.frameWhite)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          エコモード
                        </TableCell>
                        <TableCell sx={{}}>
                          {ledSoundConfDuo?.ecoMode &&
                            getClassificationName(CLSFCN.DUO_ECO_MODE.CATEGORY_CD, ledSoundConfDuo.ecoMode)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          ゲーム中もエコモード
                        </TableCell>
                        <TableCell sx={{}}>
                          {ledSoundConfDuo?.keepEcoType &&
                            getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, ledSoundConfDuo.keepEcoType)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className="bgGray" sx={{}}>
                          グループ準拠
                        </TableCell>
                        <TableCell sx={{ position: 'relative' }}>
                          {isAllCheckActive && (
                            <div className={classes.grayCoverDark} style={{ width: '251px', height: '24px' }} />
                          )}
                          {ledSoundConfDuo?.groupCompliant &&
                            getClassificationName(CLSFCN.ON_OFF.CATEGORY_CD, ledSoundConfDuo.groupCompliant)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>
          </Box>
        </MachineConfContent>
      </Layout>
    </ScreenContext.Provider>
  );
};

export default LedSoundConfDuo;
